/*
 * Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT
 *
 * Licensed under the MIT License. See the LICENSE accompanying this file
 * for the specific language governing permissions and limitations under
 * the License.
 */

/*
 * Details to use MFA you will find here:
 * https://docs.amplify.aws/lib/auth/mfa/q/platform/js
 */

import React from 'react';
import { connect } from 'react-redux';

import { Auth } from 'aws-amplify';
import { I18n } from '@aws-amplify/core';

import { makeStyles } from '@material-ui/core/styles';

import { Branding } from '../../branding';
import AppSnackbar from '../../components/Snackbar/Snackbar';
import MfaTotpConfigDialog from '../../components/MfaTotpConfigDialog/MfaTotpConfigDialog';

import awsconfig from '../../aws-exports';

const useStyles = makeStyles((theme) => ({
	root: {},
	card: {},
	cardHeader: {
		backgroundColor: Branding.primary,
		color: Branding.white,
		height: 50,
		textAlign: 'center',
	},
	cardContent: {
		textAlign: 'center',
		padding: theme.spacing(1),
	},
	buttonSave: {
		marginTop: theme.spacing(1),
		marginLeft: theme.spacing(1),
		color: Branding.positive,
		'&:hover': {
			color: Branding.positive,
			opacity: Branding.opacityHover,
		},
	},
}));

const mapStateToProps = (state) => {
	return {
		lang: state.app.lang,

		phone_number_verified: state.user.attributes.phone_number_verified || false,
	};
};

function TabMfaData(props) {
	const classes = useStyles();
	const [totpDialog, setTotpDialog] = React.useState(false);
	const [snackBarOps, setSnackBarOps] = React.useState({
		type: 'info',
		open: false,
		vertical: 'top',
		horizontal: 'center',
		autoHide: 0,
		message: '',
	});

	// Extract mfa_required directly from URL
	const queryStringParams = new URLSearchParams(window.location.search);
	const mfa_required = queryStringParams.get('mfa_required') === 'true';

	const handleForceMfaRequired = () => {
		setTotpDialog(true);
		setSnackBarOps({
			type: 'warn',
			open: true,
			vertical: 'top',
			horizontal: 'center',
			message: I18n.get('TAB_MFA_SETUP_REQUIRED'),
		});
	};

	const handleNavigate = () => {
		const queryStringParams = new URLSearchParams(window.location.search);
		const mfa_required = queryStringParams.get('mfa_required');
		const mfa_redirect = queryStringParams.get('mfa_redirect');

		if (mfa_required && mfa_redirect) {
			// if user is coming from "MFA required" step, redirect back to original requested page
			if (mfa_redirect) {
				window.location.replace(mfa_redirect);
			} else {
				window.location.replace(awsconfig.ui_url);
			}
		}
	};
	/*
	 * mfaType: SMS || TOTP || NOMFA
	 */
	const setPreferredMFA = async (mfaType) => {
		try {
			const CognitoUser = await Auth.currentAuthenticatedUser();
			const data = await Auth.setPreferredMFA(CognitoUser, mfaType);

			if (data === 'SUCCESS') {
				setSnackBarOps({
					type: 'success',
					open: true,
					vertical: 'top',
					horizontal: 'center',
					autoHide: 3000,
					message: I18n.get('TAB_MFA_DATA_MESSAGE_SET_MFATYPE_SUCCESS'),
				});
			}
		} catch (err) {
			console.log(err);

			setSnackBarOps({
				type: 'error',
				open: true,
				vertical: 'top',
				horizontal: 'center',
				autoHide: 3000,
				message: I18n.get('TAB_MFA_DATA_MESSAGE_EROR'),
			});
		}
	};

	// Post a message to the parent window when embedded in the SANA UI user-profile
	// dialog (iframe). No-op when the page is opened standalone (window.parent === window).
	const notifyParent = (type) => {
		if (window.parent && window.parent !== window) {
			window.parent.postMessage({ type }, '*');
		}
	};

	const handleTotpDialogClose = async (successful = false) => {
		setTotpDialog(false);

		if (!successful) {
			// User closed the dialog without finishing (only possible when MFA is not
			// required). If embedded in the SANA UI user-profile dialog, ask the parent
			// to close that dialog too so the user isn't left on an empty iframe.
			notifyParent('sana:mfa-dialog-close');
			return;
		}

		setSnackBarOps({
			type: 'success',
			open: true,
			vertical: 'top',
			horizontal: 'center',
			autoHide: 3000,
			message: I18n.get('TAB_MFA_DATA_MESSAGE_VERIFY_TOTP_TOKEN_SUCCESS'),
		});

		await setPreferredMFA('TOTP');

		// Setup completed: tell the parent so it can auto-close the dialog and enable
		// the "MFA required" flag for this user.
		notifyParent('sana:mfa-setup-complete');

		if (mfa_required) {
			handleNavigate();
		}
	};

	React.useEffect(() => {
		// TOTP is the only MFA type, so skip the "choose your MFA type" step and open
		// the setup dialog directly. The forced (mfa_required) path additionally shows
		// the "setup required" warning.
		if (mfa_required) {
			handleForceMfaRequired();
			return;
		}

		setTotpDialog(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mfa_required]);

	return (
		<div className={classes.root}>
			{snackBarOps.open && <AppSnackbar ops={snackBarOps} />}

			{totpDialog && <MfaTotpConfigDialog open={totpDialog} mfaRequired={mfa_required} close={(successful) => handleTotpDialogClose(successful)} />}
		</div>
	);
}

export default connect(mapStateToProps, {})(TabMfaData);
